import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import { Navbar, Nav, Container } from 'react-bootstrap';

import routes from '../../../routesDefinitions/routes';
import brandLogo from '../../../assets/brandLogo.png';
import { logOut } from '../../store/authSlice';
import { resetFilters, resetPagination } from '../../../players/store/actions';
import NotificationDropdown from './NotificationDropdown';
import './Menu.scss';

const getMenuLabels = (userRole) => {
  const isCoach = userRole === 'coach';
  const greetingDefaults = { coach: 'Coach', admin: 'Admin', assistant: 'Admin', player: 'Zawodnik' };
  return {
    greeting: isCoach ? 'Hi' : 'Cześć',
    myAccount: isCoach ? 'My Account' : 'Moje konto',
    messages: isCoach ? 'Messages' : 'Wiadomości',
    checklist: isCoach ? 'Checklist' : 'Lista zadań',
    greetingDefault: greetingDefaults[userRole] || 'Admin',
    availablePlayers: isCoach ? 'Available Players' : 'Dostępni zawodnicy',
    favorites: isCoach ? 'Favorites' : 'Ulubieni',
    recommended: isCoach ? 'Recommended' : 'Rekomendowani',
    createPlayer: isCoach ? 'Create Player' : 'Utwórz zawodnika',
    dashboard: isCoach ? 'Dashboard' : 'Panel',
    activity: isCoach ? 'Activity' : 'Aktywność',
    coaches: isCoach ? 'Coaches' : 'Trenerzy',
    checklistTemplate: isCoach ? 'Checklist Template' : 'Szablon listy',
    pendingCoaches: 'Pending Coaches',
    checklistProgress: isCoach ? 'Checklist Progress' : 'Postęp listy',
    assistants: 'Asystenci',
    dashboardOps: isCoach ? 'Dashboard Ops' : 'Panel operacyjny',
    timeEntries: isCoach ? 'Time Entries' : 'Wpisy czasu',
    documents: isCoach ? 'Documents' : 'Dokumenty',
    pipeline: isCoach ? 'Pipeline' : 'Pipeline',
    pageContent: isCoach ? 'Player Guide' : 'Przewodnik gracza',
    oversightCoach: 'Trener ↔ Sportowiec',
    oversightAssistant: 'Asystent ↔ Sportowiec',
    myPlayers: 'Moi zawodnicy',
    myTasks: 'Moje zadania',
    workTime: 'Czas pracy',
    playerInfo: isCoach ? 'Player Info' : 'Informacje',
    settings: isCoach ? 'Settings' : 'Ustawienia',
    logout: isCoach ? 'Logout' : 'Wyloguj',
  };
};

const Menu = ({
  setIsAuthorized,
  isAuthorized,
  isProfileFinished,
  userName,
  userLastName,
  userRole,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dispatch = useDispatch();
  const unreadCount = useSelector(({ conversations }) => conversations.unreadCount);
  const checklistUnreadCount = useSelector(({ checklist }) => checklist.checklistUnreadCount);
  const totalUnread = checklistUnreadCount;
  const menuLabels = getMenuLabels(userRole);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsMobile(mediaQuery.matches);
    sync();

    // Close any open menu when switching breakpoints
    const onChange = () => {
      sync();
      setExpanded(false);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    }

    // Safari fallback
    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, []);

  // Block body scroll only for mobile drawer
  useEffect(() => {
    if (isMobile && expanded) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [expanded, isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrolled = currentScrollY > 20;
      
      setScrolled(isScrolled);

      // Hide header when scrolling down, show when scrolling up
      if (currentScrollY < 10) {
        // Always show at the top
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down - hide header
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show header
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  const handleExpandChange = name => {
    if (name === 'Favorite Players' || name === 'Available Players') {
      dispatch(resetPagination());
      dispatch(resetFilters());
    }
    setExpanded(false);
  };

  const handleMenuChange = useCallback(() => {
    setExpanded(prevExpanded => !prevExpanded);
  }, []);

  const closeMenu = useCallback(() => setExpanded(false), []);
  const isPlayerView = userRole === 'player';
  const handleBrandClick = useCallback((e) => {
    if (isPlayerView) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setExpanded(false);
  }, [isPlayerView]);

  const {
    faq,
    sampleProfile,
    aboutUs,
    createPlayer,
    createAccount,
    login,
    summerTrips,
  } = routes;

  // Links split: left side of logo vs right side
  const leftLinks = [
    { name: aboutUs.name, link: aboutUs.link },
    { name: sampleProfile.name, link: sampleProfile.link },
  ];

  const rightLinks = [
    { name: faq.name, link: faq.link },
    { name: summerTrips.name, link: summerTrips.link },
  ];

  // All links for mobile drawer
  const allNavigationLinks = [...leftLinks, ...rightLinks];

  const renderLink = ({ name, link }) => (
    <div className="nav__link nav-link" key={name}>
      <NavLink
        onClick={() => handleExpandChange(name)}
        className="inactive"
        activeClassName="active"
        to={link}
      >
        {name}
      </NavLink>
    </div>
  );

  const renderNavigationLinks = () => allNavigationLinks.map(renderLink);

  const history = useHistory();
  const handleLogoutButton = () => {
    setIsAuthorized(false);
    dispatch(
      logOut(history, { key: 'authorization', storage: window.localStorage }),
    );
  };

  return (
    <>
      {/* Mobile overlay + drawer */}
      {isMobile && expanded && (
        <div
          className="menu__overlay"
          onMouseDown={closeMenu}
          onTouchStart={closeMenu}
          aria-hidden="true"
        />
      )}

      <aside
        className={`menu__drawer ${isMobile && expanded ? 'menu__drawer--open' : ''}`}
        aria-hidden={!isMobile || !expanded}
      >
        <button
          type="button"
          className="menu__drawerClose"
          onClick={closeMenu}
          aria-label="Close menu"
        >
          <i style={{marginRight: '0'}} className="fas fa-times" />
        </button>

        <Nav className="menu__drawerNav">
          {/* Authenticated links in drawer for mobile */}
          {isAuthorized && isProfileFinished && userRole === 'player' && (
            <>
              <div className="menu__drawer-greeting">
                <i className="fas fa-hand-peace" style={{ marginRight: '0.5rem' }} />
                {menuLabels.myAccount}{userName || userLastName ? ` – ${userName ? userName.charAt(0).toUpperCase() + userName.slice(1) : ''}${userLastName ? ` ${userLastName.charAt(0).toUpperCase() + userLastName.slice(1)}` : ''}` : ''}
              </div>
              <div className="nav__link nav-link">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/player/inbox"
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-envelope" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.messages}
                  {unreadCount > 0 && (
                    <span
                      style={{
                        display: 'inline-block',
                        background: '#e53e3e',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '0 6px',
                        fontSize: '10px',
                        fontWeight: '700',
                        marginLeft: '4px',
                        lineHeight: '16px',
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </NavLink>
              </div>
              <div className="nav__link nav-link">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/player/checklist"
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-tasks" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.checklist}
                  {checklistUnreadCount > 0 && (
                    <span
                      style={{
                        display: 'inline-block',
                        background: '#e53e3e',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '0 6px',
                        fontSize: '10px',
                        fontWeight: '700',
                        marginLeft: '4px',
                        lineHeight: '16px',
                      }}
                    >
                      {checklistUnreadCount}
                    </span>
                  )}
                </NavLink>
              </div>
              <div className="menu__drawer-divider" />
            </>
          )}
          {isAuthorized && isProfileFinished && userRole !== 'player' && (
            <>
              <div className="menu__drawer-greeting">
                <i className="fas fa-hand-peace" style={{ marginRight: '0.5rem' }} />
                {menuLabels.greeting}, {userName ? userName.charAt(0).toUpperCase() + userName.slice(1) : menuLabels.greetingDefault}!
              </div>
              <div className="nav__link nav-link">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/available-players"
                  onClick={() => handleExpandChange('Available Players')}
                >
                  <i className="fas fa-users" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.availablePlayers}
                </NavLink>
              </div>
              {userRole !== 'assistant' && (
                <div className="nav__link nav-link">
                  <NavLink
                    className="inactive"
                    activeClassName="active"
                    to="/favorite-players"
                    onClick={() => handleExpandChange('Favorite Players')}
                  >
                    <i className="fas fa-heart" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                    {menuLabels.favorites}
                  </NavLink>
                </div>
              )}
              {userRole !== 'assistant' && (
                <div className="nav__link nav-link">
                  <NavLink
                    className="inactive"
                    activeClassName="active"
                    to="/recommendations"
                    onClick={handleExpandChange}
                  >
                    <i className="fas fa-star" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                    {menuLabels.recommended}
                  </NavLink>
                </div>
              )}
              <div className="nav__link nav-link">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/messages"
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-envelope" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.messages}
                  {unreadCount > 0 && (
                    <span
                      style={{
                        display: 'inline-block',
                        background: '#e53e3e',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '0 6px',
                        fontSize: '10px',
                        fontWeight: '700',
                        marginLeft: '4px',
                        lineHeight: '16px',
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </NavLink>
              </div>
              <div className="menu__drawer-divider" />
            </>
          )}
          {!(isAuthorized && (userRole === 'player' || userRole === 'assistant')) && renderNavigationLinks()}
          {userRole === 'admin' && (
            <>
              <div className="nav__link nav-link" key={createPlayer.name}>
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to={createPlayer.link}
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-plus-circle" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {createPlayer.name}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="dashboard">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/admin/dashboard"
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-chart-line" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.dashboard}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="activity">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/admin/activity"
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-stream" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.activity}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="coaches">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/admin/coach-engagement"
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-users-cog" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.coaches}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="checklist-template">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/admin/checklist-template"
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-layer-group" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.checklistTemplate}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="pending-coaches">
                <NavLink className="inactive" activeClassName="active" to="/admin/pending-coaches" onClick={handleExpandChange}>
                  <i className="fas fa-user-clock" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.pendingCoaches}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="checklist-progress">
                <NavLink className="inactive" activeClassName="active" to="/admin/checklist-progress" onClick={handleExpandChange}>
                  <i className="fas fa-tasks" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.checklistProgress}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="assistants">
                <NavLink className="inactive" activeClassName="active" to="/admin/assistants" onClick={handleExpandChange}>
                  <i className="fas fa-user-cog" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.assistants}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="ops-dashboard">
                <NavLink className="inactive" activeClassName="active" to="/admin/ops-dashboard" onClick={handleExpandChange}>
                  <i className="fas fa-tachometer-alt" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.dashboardOps}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="time-entries">
                <NavLink className="inactive" activeClassName="active" to="/admin/time-entries" onClick={handleExpandChange}>
                  <i className="fas fa-clock" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.timeEntries}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="documents">
                <NavLink className="inactive" activeClassName="active" to="/admin/documents" onClick={handleExpandChange}>
                  <i className="fas fa-folder-open" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.documents}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="pipeline">
                <NavLink className="inactive" activeClassName="active" to="/admin/pipeline" onClick={handleExpandChange}>
                  <i className="fas fa-funnel-dollar" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.pipeline}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="page-content">
                <NavLink className="inactive" activeClassName="active" to="/admin/page-content" onClick={handleExpandChange}>
                  <i className="fas fa-book-open" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.pageContent}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="oversight-coach">
                <NavLink className="inactive" activeClassName="active" to="/admin/oversight/coach" onClick={handleExpandChange}>
                  <i className="fas fa-eye" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.oversightCoach}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="oversight-assistant">
                <NavLink className="inactive" activeClassName="active" to="/admin/oversight/assistant" onClick={handleExpandChange}>
                  <i className="fas fa-eye" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.oversightAssistant}
                </NavLink>
              </div>
            </>
          )}
          {userRole === 'assistant' && (
            <>
              <div className="nav__link nav-link" key="my-players">
                <NavLink className="inactive" activeClassName="active" to="/admin/my-players" onClick={handleExpandChange}>
                  <i className="fas fa-user-friends" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.myPlayers}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="my-tasks">
                <NavLink className="inactive" activeClassName="active" to="/admin/assistant-dashboard" onClick={handleExpandChange}>
                  <i className="fas fa-clipboard-list" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.myTasks}
                </NavLink>
              </div>
              <div className="nav__link nav-link" key="work-time">
                <NavLink className="inactive" activeClassName="active" to="/admin/time-tracking" onClick={handleExpandChange}>
                  <i className="fas fa-clock" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.workTime}
                </NavLink>
              </div>
            </>
          )}
          {isAuthorized && isProfileFinished && userRole === 'player' && (
            <div className="nav__link nav-link" key="player-info">
              <NavLink className="inactive" activeClassName="active" to="/player/info" onClick={handleExpandChange}>
                <i className="fas fa-info-circle" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                {menuLabels.playerInfo}
              </NavLink>
            </div>
          )}
          {isAuthorized ? (
            <>
              <div className="menu__drawer-divider" />
              <div className="nav__link nav-link">
                <NavLink
                  className="inactive"
                  activeClassName="active"
                  to="/finish-profile"
                  onClick={handleExpandChange}
                >
                  <i className="fas fa-cog" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.settings}
                </NavLink>
              </div>
              <div className="nav__link nav-link">
                <NavLink
                  className="inactive"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExpandChange();
                    handleLogoutButton();
                  }}
                >
                  <i className="fas fa-sign-out-alt" style={{ marginRight: '0.75rem', width: '20px', textAlign: 'center' }} />
                  {menuLabels.logout}
                </NavLink>
              </div>
            </>
          ) : (
            <>
              <div className="nav__link nav-link nav__link--button">
                <NavLink
                  className="inactive nav__button nav__button--outline"
                  activeClassName="active"
                  to={login.link}
                  onClick={handleExpandChange}
                >
                  Login
                </NavLink>
              </div>
              <div className="nav__link nav-link nav__link--button">
                <NavLink
                  className="inactive nav__button nav__button--primary"
                  activeClassName="active"
                  to={createAccount.link}
                  onClick={handleExpandChange}
                >
                  {createAccount.name}
                </NavLink>
              </div>
            </>
          )}
        </Nav>
      </aside>

      <div className={`Menu ${isVisible ? 'visible' : 'hidden'}`}>
        <Navbar
          expanded={!isMobile ? expanded : false}
          bg="light"
          expand="md"
          className={scrolled ? 'scrolled' : ''}
        >
          <Container>
          {isMobile ? (
            /* ── Mobile: logo left, [bell] hamburger right ── */
            <>
              <NavLink className="nav__brand" exact to="/" onClick={handleBrandClick}>
                <Navbar.Brand>
                  <img className="navbar-brand" src={brandLogo} alt="Play Sport Usa Logo" />
                </Navbar.Brand>
              </NavLink>
              {isAuthorized && isProfileFinished && (
                <div className="menu__bell-wrapper" style={{ marginLeft: 'auto', marginRight: '8px' }}>
                  <button
                    type="button"
                    className="menu__bell"
                    onClick={() => setDropdownOpen(prev => !prev)}
                    aria-label={`Notifications${totalUnread > 0 ? `, ${totalUnread} unread` : ''}`}
                  >
                    <i className="fas fa-bell" />
                    {totalUnread > 0 && (
                      <span className="menu__bell-badge">
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    )}
                  </button>
                  {dropdownOpen && (
                    <NotificationDropdown
                      userRole={userRole}
                      onClose={() => setDropdownOpen(false)}
                    />
                  )}
                </div>
              )}
              <button
                type="button"
                className="menu__mobile-toggle"
                onClick={handleMenuChange}
                aria-label="Open menu"
                style={{ marginLeft: isAuthorized && isProfileFinished ? 0 : 'auto' }}
              >
                <i className="fas fa-bars" />
              </button>
            </>
          ) : (
            /* ── Desktop: [left links] [logo] [right links + buttons] ── */
            <div className="nav__three-col">
              <Nav className="nav__left" style={{ alignItems: 'center' }}>
                {!(isAuthorized && (userRole === 'player' || userRole === 'assistant')) && leftLinks.map(renderLink)}
              </Nav>

              <NavLink className="nav__brand" exact to="/" onClick={handleBrandClick}>
                <Navbar.Brand>
                  <img className="navbar-brand" src={brandLogo} alt="Play Sport Usa Logo" />
                </Navbar.Brand>
              </NavLink>

              <Nav className="nav__right" style={{ alignItems: 'center' }}>
                {!(isAuthorized && (userRole === 'player' || userRole === 'assistant')) && rightLinks.map(renderLink)}
                {isAuthorized ? null : (
                  <>
                    <div className="nav__link nav-link nav__link--button">
                      <NavLink
                        className="inactive nav__button nav__button--outline"
                        activeClassName="active"
                        to={login.link}
                        onClick={handleExpandChange}
                      >
                        Login
                      </NavLink>
                    </div>
                    <div className="nav__link nav-link nav__link--button">
                      <NavLink
                        className="inactive nav__button nav__button--primary"
                        activeClassName="active"
                        to={createAccount.link}
                        onClick={handleExpandChange}
                      >
                        {createAccount.name}
                      </NavLink>
                    </div>
                  </>
                )}
              </Nav>
            </div>
          )}
        </Container>
      </Navbar>
      </div>
    </>
  );
};

export default Menu;
